// 2026-02 · Utilidades compartidas para mostrar ingredientes en la app y en PDF.
// - normalizeIngredientName: acorta nombres genéricos (proteína en polvo, etc.)
// - formatAmount: cambia 'g' → 'gramos' respetando otras unidades (ml, cdta, etc.)
// - shouldExcludeIngredient: filtro estricto para mostrar sólo alimentos principales
//   (excluye condimentos, especias, salsas, aceites <10g, cualquier ingrediente <10g,
//   ingredientes 'opcional/al gusto' y vegetales <30g).

const PROTEIN_POWDER_REGEX = /\b(prote[ií]na\s+(?:en\s+polvo|whey|isolate|concentrada?|hidrolizada?|iso|impact|vegana?|de\s+suero|de\s+guisante|de\s+arroz)|whey(?:\s+protein)?|protein\s+powder|casein[a]?)\b/i;

// Normalización básica para comparar (sin tildes / minúsculas).
const _norm = (s) => (s || '').toString().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Condimentos, especias, hierbas aromáticas y aditivos menores. Se aplica
// exclusión total: no importa la cantidad, se descartan siempre.
const CONDIMENT_REGEX = /\b(sal|pimienta|especias?|extracto|esencia|spray|comino|p[áa]prika|paprika|or[ée]gano|romero|tomillo|laurel|canela|nuez\s+moscada|curry|c[úu]rcuma|cayena|hierbas?|cardamomo|clavo(?:\s+de\s+olor)?|azafr[áa]n|edulcorante|stevia|sucralosa|polvo\s+de\s+hornear|bicarbonato|ajo|perejil|cilantro|jengibre|albahaca|menta|hierbabuena|saz[óo]n|sazonador|condimento|vainilla|colorante)\b/i;

// Salsas y líquidos secundarios. Exclusión total, sin importar la cantidad.
const SAUCE_REGEX = /(salsa\s+(?:de\s+)?(?:soya|soja|inglesa|picante|bbq|barbacoa|tomate|de\s+tomate)|\bsalsa\b|vinagre|mostaza|ketchup|mayonesa|zumo\s+de\s+lim[óo]n|jugo\s+de\s+lim[óo]n|lim[óo]n\s*\(\s*jugo\s*\)|\blim[óo]n\s+jugo\b)/i;

// Palabras clave para identificar aceites (se excluyen si cantidad < 10 o sin cantidad).
const OIL_REGEX = /\baceite\b/i;

// Palabras clave para identificar vegetales/verduras. Umbral especial: si es
// vegetal y la cantidad es < 30 g/ml, se excluye.
const VEG_REGEX = /\b(espinaca|lechuga|arugula|r[úu]cula|kale|acelga|br[óo]coli|coliflor|zanahoria|tomate|pepino|pimiento|pimet[óo]n|pimenton|morr[óo]n|cebolla|apio|calabac[íi]n|calabaza|zapallo|berenjena|esparragos?|col|repollo|remolacha|betarraga|r[áa]bano|champi[ñn]ones?|hongos?|vegetal(?:es)?|verduras?|ensalada|jitomate|chile|aji)\b/i;

// Marcadores 'opcional' / 'al gusto'.
const OPTIONAL_REGEX = /\b(opcional|al\s+gusto|a\s+gusto)\b/i;

// Agua y hielo: liquidos secundarios que deben excluirse siempre, sin
// importar la cantidad (antes no habia regla para ellos y se colaban en el PDF).
const WATER_REGEX = /\b(agua|hielo)\b/i;

// 2026-10 - pescado/atun enlatado "en agua" o "en aceite" (medio de conservacion):
// el agua/aceite aqui no es un ingrediente liquido secundario, es parte del nombre
// del producto. No debe dispararse WATER_REGEX ni OIL_REGEX para estos casos.
const SEAFOOD_IN_LIQUID_REGEX = /\b(atun|sardinas?|salmon|caballa|pescado)\b.*\b(en|al)\s+(agua|aceite)\b/i;

export function normalizeIngredientName(name) {
  if (!name) return '';
  const s = String(name).trim();
  if (PROTEIN_POWDER_REGEX.test(s)) return 'Proteína en polvo';
  return s;
}

export function formatAmount(amount) {
  if (amount == null || amount === '') return '';
  let s = String(amount).trim();
  // Reemplaza unidades " g" (posiblemente al final o antes de paréntesis) por " gramos".
  s = s.replace(/(\d(?:[.,]\d+)?)\s*g\b(?!ramos|r\b|amma)/gi, '$1 gramos');
  return s;
}

export function parseAmountNumber(amount) {
  if (amount == null) return null;
  const m = String(amount).match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) : null;
}

// 2026-02 · Filtro estricto: devuelve true si el ingrediente debe OCULTARSE.
// Regla de negocio confirmada por el usuario:
//  - Excluir condimentos, especias, hierbas aromáticas.
//  - Excluir salsas y líquidos secundarios (soya, vinagre, mostaza, jugo limón).
//  - Excluir aceites con cantidad < 10 g/ml (o sin cantidad).
//  - Excluir cualquier ingrediente con cantidad < 10 g/ml.
//  - Excluir ingredientes 'opcional' / 'al gusto'.
//  - Excluir vegetales con cantidad < 30 g.
//  - Si el ingrediente no tiene cantidad, sólo se excluye cuando su nombre es
//    condimento / salsa / aceite (regla c del usuario). Proteína/carb/grasa
//    principal sin gramaje se dejan visibles con el nombre solo.
// 2026-10 - cantidades en PIEZAS ("2 unidades", "2 huevos", "1 arepa"): el
// numero no son gramos. Antes "2 unidades" de huevo caia en la regla "< 10" y
// el huevo desaparecia de la tarjeta y del PDF aunque sus macros si contaban.
const COUNT_UNIT_REGEX = /\d\s*(?:unidad(?:es)?|unds?|uds?|u\b|huevos?|claras?|rebanadas?|tajadas?|lonjas?|lonchas?|piezas?|arepas?|arepitas?|tortillas?|tostadas?)/i;
const COUNTABLE_FOOD_REGEX = /\b(huevos?|claras?|arepas?|arepitas?|tortillas?|pan|tostadas?|bananos?|bananas?|manzanas?)\b/i;

export function shouldExcludeIngredient(name, amount) {
  if (!name) return true;
  const raw = String(name);
  const n = _norm(raw);

  // 2026-09 - proteina en polvo con sabor (ej. "... sabor vainilla") no debe
  // excluirse solo porque el sabor matchea CONDIMENT_REGEX ("vainilla").
  if (PROTEIN_POWDER_REGEX.test(raw)) return false;

  // 2026-10 - atun/sardinas/salmon "en agua" o "en aceite" (conserva): no excluir
  // por WATER_REGEX/OIL_REGEX, el liquido es el medio de conservacion del pescado.
  if (SEAFOOD_IN_LIQUID_REGEX.test(n)) return false;

  if (OPTIONAL_REGEX.test(raw)) return true;
  if (CONDIMENT_REGEX.test(n)) return true;
  if (SAUCE_REGEX.test(n)) return true;
  if (WATER_REGEX.test(n)) return true;

  const bareNumber = /^\s*\d+(?:[.,]\d+)?\s*$/.test(String(amount ?? ''));
  const isCount = COUNT_UNIT_REGEX.test(_norm(amount || ''))
    || (bareNumber && COUNTABLE_FOOD_REGEX.test(n) && parseAmountNumber(amount) <= 6);
  const num = isCount ? null : parseAmountNumber(amount);
  const isOil = OIL_REGEX.test(n);
  if (isOil) {
    if (num == null || num < 10) return true;
  }
  const isVeg = VEG_REGEX.test(n);
  if (isVeg && num != null && num < 30) return true;

  if (num != null && num < 10) return true;
  return false;
}

export function formatIngredientLine(name, amount) {
  const n = normalizeIngredientName(name);
  const a = formatAmount(amount);
  if (!n && !a) return '';
  if (!a) return n;
  return `${n} ${a}`;
}
